import { useMutation, useQuery } from '@tanstack/react-query'
import { authFetch } from '@/api/client'
import { ApiError, type ProblemDetail } from '@/lib/errors'

/**
 * The AI half of the "Novo canal" dialog: name/handle suggestions, YouTube
 * handle availability and the avatar brief. The channel itself is still
 * created on YouTube by hand (Data API v3 has no ``channels.insert``) and
 * registered by the regular ``POST /api/v1/channels``.
 */

/** ``unknown`` = the lookup couldn't answer (no API key, quota, TikTok). */
export type HandleStatus = 'available' | 'taken' | 'invalid' | 'unknown'

export interface ChannelSuggestion {
  name: string
  handle: string
  rationale: string
  handle_status: HandleStatus
}

export interface ChannelSuggestResponse {
  suggestions: ChannelSuggestion[]
  avatar_prompt: string
}

export interface SuggestChannelInput {
  description: string
  platform: 'youtube' | 'tiktok'
}

export interface HandleCheck {
  handle: string
  status: HandleStatus
}

export interface HandleAlternativesInput {
  name: string
  description: string
  exclude: string[]
}

export interface AvatarPromptInput {
  name: string
  description: string
}

/** Mirrors the backend's ``HANDLE_RE``: checked here so a typo never costs a request. */
export const HANDLE_RE = /^[a-z0-9._-]{3,30}$/

export function normalizeHandle(raw: string): string {
  return raw.trim().toLowerCase().replace(/^@+/, '')
}

async function readProblem(res: Response, fallback: string): Promise<never> {
  let problem: ProblemDetail | null = null
  const contentType = res.headers.get('content-type') ?? ''
  if (
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json')
  ) {
    try {
      const body = (await res.json()) as ProblemDetail | null
      if (body && typeof body.title === 'string') problem = body
    } catch {
      /* ignore */
    }
  }
  const message = problem?.detail ?? problem?.title ?? `${fallback} (HTTP ${String(res.status)})`
  throw new ApiError(message, res.status, problem, null)
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await authFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })
  if (!res.ok) await readProblem(res, fallback)
  const json = (await res.json()) as { data: T }
  return json.data
}

/**
 * Name/handle/avatar suggestions from a free-form description. Handles come
 * back already checked on YouTube; taken ones trigger a second LLM round on
 * the backend, so expect 3-10s.
 */
export function useSuggestChannel() {
  return useMutation({
    mutationFn: (input: SuggestChannelInput) =>
      postJson<ChannelSuggestResponse>(
        '/api/v1/tools/channel-creator/suggest',
        input,
        'Falha ao gerar sugestões',
      ),
  })
}

/**
 * Is ``handle`` free on YouTube? Pass the already-debounced, normalized
 * handle; ``enabled`` stays off for invalid ones (decided client-side).
 */
export function useHandleStatus(handle: string, enabled: boolean) {
  return useQuery({
    queryKey: ['tools', 'channel-creator', 'handle-status', handle],
    queryFn: async (): Promise<HandleCheck> => {
      const res = await authFetch(
        `/api/v1/tools/channel-creator/handle-status?handle=${encodeURIComponent(handle)}`,
        { credentials: 'same-origin' },
      )
      if (!res.ok) await readProblem(res, 'Falha ao verificar o @')
      const json = (await res.json()) as { data: HandleCheck }
      return json.data
    },
    enabled: enabled && HANDLE_RE.test(handle),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** Free alternative handles for a chosen name whose handle is taken. */
export function useHandleAlternatives() {
  return useMutation({
    mutationFn: (input: HandleAlternativesInput) =>
      postJson<{ alternatives: HandleCheck[] }>(
        '/api/v1/tools/channel-creator/handles',
        input,
        'Falha ao gerar opções de @',
      ).then((d) => d.alternatives),
  })
}

/** Only the avatar brief — for regenerating the photo after editing the description. */
export function useAvatarPrompt() {
  return useMutation({
    mutationFn: (input: AvatarPromptInput) =>
      postJson<{ avatar_prompt: string }>(
        '/api/v1/tools/channel-creator/avatar-prompt',
        input,
        'Falha ao gerar o briefing do avatar',
      ).then((d) => d.avatar_prompt),
  })
}
